import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Clock, Calendar, Check } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SurgeryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];
  const [surgery, setSurgery] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSurgery();
  }, [id]);

  const fetchSurgery = async () => {
    try {
      const response = await axios.get(`${API}/surgeries/${id}`);
      setSurgery(response.data);
    } catch (error) {
      console.error('Error fetching surgery:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!surgery) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-xl text-gray-500">Surgery not found</p>
      </div>
    );
  }

  return (
    <main className="py-12 bg-gradient-to-br from-gray-50 to-white min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link to="/surgeries" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-8">
          <ArrowLeft className="h-5 w-5 mr-2" />
          {t.backToSurgeries}
        </Link>

        {/* Surgery Header */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-8 text-white">
            <h1 className="text-3xl md:text-4xl font-bold mb-4" data-testid="surgery-name">
              {surgery[`name_${language}`]}
            </h1>
            <div className="flex flex-wrap gap-6 text-blue-100">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>{t.duration}: {surgery.duration_days} {t.days}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <span>{t.recovery}: {surgery.recovery_days} {t.days}</span>
              </div>
            </div>
          </div>

          <div className="p-8">
            {/* Description */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t.description}</h2>
              <p className="text-gray-700 leading-relaxed" data-testid="surgery-description">
                {surgery[`description_${language}`]}
              </p>
            </div>

            {/* Included */}
            {surgery[`includes_${language}`] && surgery[`includes_${language}`].length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">{t.included}</h2>
                <ul className="space-y-3" data-testid="surgery-includes">
                  {surgery[`includes_${language}`].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-3 w-3 text-green-600" />
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Pricing */}
            <div className="bg-blue-50 rounded-xl p-6 mb-8">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{t.from}</p>
                  <div className="flex items-baseline gap-4">
                    <p className="text-4xl font-bold text-blue-600" data-testid="surgery-price-eur">
                      €{surgery.price_eur}
                    </p>
                    <p className="text-2xl text-gray-500" data-testid="surgery-price-usd">
                      ${surgery.price_usd}
                    </p>
                  </div>
                </div>
                <Button
                  size="lg"
                  onClick={() => navigate('/quote', { state: { surgery } })}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 rounded-full"
                  data-testid="request-quote-btn"
                >
                  {t.requestQuote}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SurgeryDetail;